import React, {memo, useState} from "react";
import styles from "./MovieCard.module.css";
import {Card, CardContent, IconButton, Popover, Skeleton, Stack, Typography} from "@mui/material";
import CustomChip from "../../Chip/Chip";
import { Plus, Star, Top10, Top10Mobile} from "../../../assets/files";
import {IData, IRelated} from "../types";
import {useLocation, useNavigate} from "react-router-dom";
import {routes} from "../../../routes/routes";
import Keys from "../../../constants/helper";
import {useDispatch} from "react-redux";
import {AppDispatch} from "../../../store";
import {openVideoPlayer, closeVideoPlayer} from "../../../store/player/playerSlice";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {useNavTitle} from "../../Aside/IAside";
import useResize from "../../../hooks/useResize";
import {enqueueSnackbar} from "notistack";
import {snackbarPosBottomRight} from "../../Notification/types";
import {formatToOneDecimal, getProgramState, getProgramStatus, handlePlayCustom} from "../../../utils";
import {IProfileList, useAddToListMutation, useLazyGetContentDetailsQuery} from "../../../api/modules";
import {contentQueries} from "../../../api/modules/content/content.queries";
import {MOBILE_VERSION} from "../../../constants";
import {IContentContext} from "../../../store/player/playerSlice";
import {ProgramPages} from "../../../types";
import LiveBadge from "../../LiveBadge";


interface IProps {
    data: IData | IRelated | IProfileList | any;
    isProgram?: boolean;
    rank?: number | null;
    directPlay?: boolean;
    contentContext?: IContentContext | null;
    isSportsCard?: boolean;
}


const MovieCard: React.FC<IProps> = (props) => {
    const {innerWidth} = useResize()
    const {t} = useTranslation("translations");
    const {data, isProgram, rank, directPlay, contentContext, isSportsCard} = props;
    const navTitles = useNavTitle();
    const profileID = localStorage.getItem(Keys.EPIC_PROFILE_ID) as string;
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate()
    const location = useLocation();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const genreArray = data?.genders && data?.genders.split(",");
    const [isImageLoading, setImageLoading] = useState(true);
    const handleImageLoad = () => setImageLoading(false);
    const [isPosterLoading, setPosterLoading] = useState(true);
    const handlePosterLoad = () => setPosterLoading(false);
    const isMobile = innerWidth < MOBILE_VERSION
    const [handleAddToList] = useAddToListMutation()
    const [getContentDetails] = useLazyGetContentDetailsQuery()
    const prefetchContentDetails = contentQueries.usePrefetch("getContentDetails");
    const role = localStorage.getItem(Keys.EPIC_USER_ROLE);
    const [_isLoader, setIsLoader] = useState<boolean>(false)
    const seasons = data?.season_count ?? 0
    const isSerial = seasons > 0 && data?.type === "SERIE";
    const programState = getProgramState(data.start, data.end)

    const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
        if (directPlay || isSportsCard) {
            handleFullScreen();
            return;
        }   

        if (isMobile && !isProgram) {
            handleNavigate(data?.id)
        } else if (isMobile && isProgram) {
            handleFullScreen()
        } else {
            if (anchorEl === event.currentTarget) {
                setAnchorEl(null);
            } else {
                setAnchorEl(event.currentTarget);
            }
        }

    };

    const handleNavigate = (id: number | string | undefined) => {
        if (isProgram && !programState.isStarted) {
            setAnchorEl(null);
            return;
        }

        if (isProgram) {
            handleFullScreen()
        } else {
            navigate(`${routes.details.details}/${id}`, {
                state: {fromPath: `${location.pathname}${location.search}`},
            })
        }
    }

    const handleAddList = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            if (profileID && data?.id) {
                await handleAddToList({
                    params: {profileID: profileID},
                    body: {content: data?.id as number}
                }).unwrap();
                enqueueSnackbar(t("Messages.addedToMyListSuccess", {title: data?.title}), {
                    anchorOrigin: snackbarPosBottomRight,
                    variant: "success"
                });
            }
        } catch (error) {
            error && enqueueSnackbar(t("Messages.addedToMyListError", {title: data?.title}), {
                anchorOrigin: snackbarPosBottomRight,
                variant: "error"
            });
        }
    }


    const handleFullScreen = async () => {
        if (isProgram && !programState.isStarted) {
            setAnchorEl(null);
            return;
        }

        // Open modal immediately with card data so the user sees the player at once.
        // handlePlayCustom will dispatch openVideoPlayer again with the real video URL
        // once the API responds, causing ReactPlayer to mount and start loading.
        dispatch(openVideoPlayer({
            id: Number(data.id),
            title: data.title || "",
            name: data.title || "",
            poster_path: data.poster_path || data.backdrop_path || "",
            type: data.type || "",
            videoData: { id: null, url: "", subtitle: "" },
            contentContext,
        }));

        handlePlayCustom({
            profileId: profileID,
            data: data.id,
            dispatch,
            setIsLoader,
            getContentDetails,
            openVideoPlayer,
            contentContext,
            onError: () => dispatch(closeVideoPlayer()),
        });
    }

    const getLabelDurationOrSeason = () => {
        if (!isSerial) return data?.duration ?? "";
        const label = `${seasons} ${t("Details.season").toLowerCase()}`
        return `${label}${seasons > 1 ? "s" : ""}`
    }

    const translatedTitle = navTitles.find(item => item.name === data?.type)?.label || data?.type;
    const imagePath = !isProgram ? data?.poster_path : data?.backdrop_path;

    return (
        <>
            <div
                className={clsx(
                    styles.card,
                    rank && styles.cardTOP,
                    isProgram && styles.cardProgram,
                    isSportsCard && styles.cardSportsProgram,
                )}
                onClick={handleCardClick}
                onTouchStart={() => {
                    if ((data?.type !== ProgramPages.LIVE && data?.type !== ProgramPages.SPORTS) || !data?.id || !profileID) return;
                    prefetchContentDetails({id: Number(data.id), role, profile_id: profileID}, {ifOlderThan: 60});
                }}
            >
                {isImageLoading && (
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        className={clsx(
                            styles.poster,
                            isProgram && styles.posterProgram,
                            isSportsCard && styles.posterSportsProgram,
                            isSportsCard && styles.sportsCardMedia
                        )}
                    />
                )}
                {isSportsCard ? (
                    <div className={clsx(styles.sportsCardContent, isImageLoading ? styles.hidden : "")}>
                        {programState.isRunning && (
                            <LiveBadge label={t("Details.live")} className={styles.sportsLiveBadge}/>
                        )}
                        <img
                            src={imagePath}
                            alt={"MovieItem"}
                            onLoad={handleImageLoad}
                            className={styles.sportsCardImage}
                        />
                        <div className={styles.sportsCardInfo}>
                            <Typography variant="subtitle2" className={styles.sportsCardTitle}>
                                {data?.title}
                            </Typography>
                            <Typography variant="caption" className={styles.sportsCardStatus}>
                                {getProgramStatus(data.start, data.end)}
                            </Typography>
                        </div>
                    </div>
                ) : (
                    <Stack direction={"row"}>
                        {!isImageLoading && rank && <div className={styles.numberContainer}>
                          <span className={styles.top10Label}>
                            {isMobile ? <Top10Mobile/> : <Top10/>}</span>
                          <span className={styles.glowingNumber}>{rank}</span>
                        </div>}
                        <img
                            src={imagePath}
                            alt={"MovieItem"}
                            onLoad={handleImageLoad}
                            className={clsx(
                                styles.poster,
                                rank && styles.posterTOP,
                                isProgram && styles.posterProgram,
                                isImageLoading ? styles.hidden : ""
                            )}
                        />
                  </Stack>
                )}

                {/*{data?.type === "LIVE" &&*/}
                {/*  <div className={clsx(styles.chipLive)}>*/}
                {/*    <CustomChip label={t("Details.live")} color="error"/>*/}
                {/*  </div>}*/}


                {/*{isProgram && <div className={styles.posterProgramSubtitle}>*/}
                {/*  <Typography variant={"h4"}>{data?.title}</Typography>*/}
                {/*  <Typography variant={"body1"}>Continue: 30m</Typography>*/}
                {/*</div>}*/}


                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                >
                    <Card className={styles.popover_card} onClick={() => handleNavigate(data?.id)}>
                        <span className={styles.type}>{translatedTitle}</span>
                        {isPosterLoading && (
                            <Skeleton variant="rectangular" animation="wave" className={styles.cardImage}/>)}

                        <img
                            src={data?.backdrop_path}
                            alt={"backdrop_path"}
                            onLoad={handlePosterLoad}
                            className={clsx(
                                styles.cardImage,
                                isPosterLoading ? styles.hidden : "",
                            )}
                        />

                        <CardContent
                            className={styles.content}>

                            <Typography gutterBottom variant="h5" component="h5">
                                {data?.title}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems={"center"} gap={1}>
                                {data?.rating &&
                                  <div className={styles.rating}><Star/> {formatToOneDecimal(data?.rating)}</div>}
                                {data?.duration &&
                                  <CustomChip label={getLabelDurationOrSeason()} color={"default"} variant={"filled"}/>}
                                {data?.year &&
                                  <CustomChip label={data?.year.toString()} color={"default"} variant={"filled"}/>}
                                {data?.country &&
                                  <CustomChip label={data?.country} color={"default"} variant={"filled"}/>}
                                {/*<CustomChip label={"Full HD"} color={"secondary"} variant={"filled"}/>*/}
                                {data?.classification &&
                                  <CustomChip label={data?.classification} color={"primary"} variant={"filled"}/>}
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems={"center"} mt={2} flexWrap={"wrap"} gap={1}>
                                {genreArray && genreArray?.map((genre: string) => (
                                    <CustomChip key={genre} label={genre} color={"default"} variant={"filled"}/>
                                ))}
                            </Stack>

                            {isProgram &&
                              <span className={styles.continueField}>
                                 {getProgramStatus(data.start, data.end)}
                              </span>}


                            <Stack direction="row" spacing={1} className={styles.controls}>
                                {!isProgram && <>
                                  <div className={styles.controlsLeft}>
                                    <IconButton onClick={handleAddList}><Plus/></IconButton>
                                  </div>
                                </>}
                            </Stack>

                        </CardContent>
                    </Card>
                </Popover>
            </div>
        </>
    );
};

const propsAreEqual = (prevProps: IProps, nextProps: IProps) => {
        // 1. Comparación de 'data':
        // Lo más eficiente es comparar por un identificador único (ID). 
        // Esto evita re-renderizados si la referencia del objeto 'data' cambia pero es la misma película.
        let isDataEqual = prevProps.data?.id === nextProps.data?.id;

        // IMPORTANTE: Si es un 'Program' (TV en vivo) y las fechas de start/end pueden actualizarse
        // en tiempo real manteniendo el mismo ID, deberías comparar esas propiedades explícitamente 
        // para que la UI se actualice (ej: getProgramStatus y getProgramState).
        if (prevProps.isProgram || nextProps.isProgram) {
            isDataEqual = isDataEqual && 
                            prevProps.data?.start === nextProps.data?.start &&
                            prevProps.data?.end === nextProps.data?.end;
        }

        // 2. Comparación de valores primitivos:
        // Al ser booleanos o números, una comparación estricta (===) es rápida y segura.
        const isProgramEqual = prevProps.isProgram === nextProps.isProgram;
        const isRankEqual = prevProps.rank === nextProps.rank;
        const isDirectPlayEqual = prevProps.directPlay === nextProps.directPlay;

        // 3. Comparación de 'contentContext':
        // Si 'contentContext' se regenera seguido en el componente padre (generando nuevas referencias),
        // hacer '===' causará re-renderizados innecesarios. Si tiene un ID interno, compáralo por ID.
        // Si asumes que es estable o inmutable, una comparación estricta está bien.
        const isContextEqual = prevProps.contentContext === nextProps.contentContext;

        // Retorna true si TODOS son iguales (no re-renderiza), false si ALGUNO cambió (re-renderiza)
        return isDataEqual && isProgramEqual && isRankEqual && isDirectPlayEqual && isContextEqual;
};

export default memo(MovieCard, propsAreEqual);
